import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import { api, errMsg, inviteText, LANGS, origin, waUrl } from "../lib/api";
import { useApp, useData } from "../lib/app";
import { Avatar, Chips, Header, Sheet } from "../components/bits";
import { FREE, GOOD, RELS } from "./Start";
import { useT, UI_LANGS } from "../lib/i18n";

export function AajiButton() {
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const t = useT();
  const open = async () => { try { setP((await api.get(`/demo/aaji?origin=${encodeURIComponent(origin())}`)).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <button data-testid="aaji-preview-btn" onClick={open} className="ul-btn ul-wa w-full">{t("💬 See what Aaji gets on WhatsApp")}</button>
      <Sheet open={!!p} onClose={() => setP(null)} testid="aaji-preview-sheet">
        {p && (p.text ? (
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden" style={{ background: "#ECE5DD" }}>
              <div className="px-4 py-3 text-white font-bold" style={{ background: "#075E54" }}>← Meera 🙏</div>
              <div className="p-4"><div className="bg-white rounded-xl rounded-tl-none p-3 shadow-sm max-w-[90%] whitespace-pre-wrap [overflow-wrap:anywhere] text-[15px]" data-testid="aaji-wa-text">{p.text}</div></div>
            </div>
            <button data-testid="aaji-open-done-btn" className="ul-btn ul-primary w-full" onClick={() => nav(`/d/${p.token}`)}>{t("Open Aaji's Done page")}</button>
            <p className="text-xs text-center text-[#6B6680]">Opens without login. Tapping Done there really completes '{p.title}'.</p>
          </div>
        ) : <p className="text-center py-6">Aaji has no open tasks right now 🌸 Reset the demo to try again.</p>)}
      </Sheet>
    </>
  );
}

function AddMember({ open, onClose, child }) {
  const { refresh } = useApp();
  const [f, setF] = useState({ name: "", relation: "", group: "adult", mode: "app", language: "English", note: "", age: "" });
  const save = async () => {
    const body = child || f.group === "child" ? { type: "child", name: f.name, age: Number(f.age) || null, relation: f.relation }
      : { ...f, type: f.group === "elder" && f.mode === "whatsapp" ? "whatsapp" : "app", elder: f.group === "elder" };
    try { await api.post("/members", body); toast.success(`${f.name} added 💜`); refresh(); onClose(); setF({ ...f, name: "", note: "", age: "" }); } catch (e) { toast.error(errMsg(e)); }
  };
  const isChild = child || f.group === "child";
  return (
    <Sheet open={open} onClose={onClose} testid="add-member-sheet">
      <div className="space-y-3">
        <h3 className="font-display text-xl font-bold">{child ? "Add a child" : "Add a family member"}</h3>
        <input data-testid="add-member-name" className="ul-input" placeholder="First name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        {!child && <Chips multi={false} options={[["adult", "Adult"], ["elder", "Elder 60+"], ["child", "Child under 18"]]} value={f.group} onChange={(v) => setF({ ...f, group: v || "adult" })} testid="add-member-group" />}
        {isChild ? (<>
          <input data-testid="add-member-age" type="number" className="ul-input" placeholder="Age" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} />
          <p className="text-xs text-[#6B6680]">Children: first name and age only, no login, never given tasks. By adding a child, you confirm you are their parent or lawful guardian. <Link className="underline" to="/legal/children">Children and UnloadIt</Link></p>
        </>) : (<>
          <Chips multi={false} options={RELS} value={f.relation} onChange={(v) => setF({ ...f, relation: v || "" })} testid="add-member-rel" />
          {f.group === "elder" && <Chips multi={false} options={[["app", "Will use the app"], ["whatsapp", "WhatsApp only"]]} value={f.mode} onChange={(v) => setF({ ...f, mode: v || "app" })} testid="add-member-mode" />}
          <p className="ul-label">Messages in</p>
          <Chips multi={false} options={LANGS} value={f.language} onChange={(v) => setF({ ...f, language: v || "English" })} testid="add-member-lang" />
          <input className="ul-input" placeholder="When free / what they can do" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </>)}
        <button data-testid="add-member-save" disabled={!f.name} onClick={save} className="ul-btn ul-primary w-full">Add</button>
      </div>
    </Sheet>
  );
}

function Profile({ open, onClose }) {
  const { me, refresh } = useApp();
  const [f, setF] = useState({ language: me.me.language || "English", note: me.me.note || "", free_on: me.me.free_on || [], good_at: me.me.good_at || [] });
  const save = async () => { try { await api.patch(`/members/${me.me.id}`, f); toast.success("Saved"); refresh(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Sheet open={open} onClose={onClose} testid="profile-sheet">
      <div className="space-y-3">
        <h3 className="font-display text-xl font-bold">My profile</h3>
        <p className="ul-label">Messages in</p><Chips multi={false} options={LANGS} value={f.language} onChange={(v) => setF({ ...f, language: v || "English" })} testid="profile-lang" />
        <p className="ul-label">When I'm free / what I can do</p>
        <input className="ul-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="profile-note" />
        <Chips options={FREE} value={f.free_on} onChange={(v) => setF({ ...f, free_on: v })} testid="profile-free" />
        <Chips options={GOOD} value={f.good_at} onChange={(v) => setF({ ...f, good_at: v })} testid="profile-good" />
        <button data-testid="profile-save" onClick={save} className="ul-btn ul-primary w-full">Save</button>
      </div>
    </Sheet>
  );
}

function HomeSettings({ open, onClose }) {
  const { me, refresh } = useApp();
  const [milk, setMilk] = useState(me.home.milk || {});
  const [food, setFood] = useState(me.home.food || {});
  const save = async () => { try { await api.patch("/home", { milk, food }); toast.success("Saved"); refresh(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Sheet open={open} onClose={onClose} testid="home-settings-sheet">
      <div className="space-y-3">
        <h3 className="font-display text-xl font-bold">🥛 Milk account</h3>
        <input className="ul-input" placeholder="Dudhwala's name" value={milk.name || ""} onChange={(e) => setMilk({ ...milk, name: e.target.value })} data-testid="milk-name" />
        <div className="flex gap-2">
          <input type="number" className="ul-input" placeholder="Litres / day" value={milk.litres || ""} onChange={(e) => setMilk({ ...milk, litres: Number(e.target.value) })} data-testid="milk-litres" />
          <input type="number" className="ul-input" placeholder="₹ / litre" value={milk.rate || ""} onChange={(e) => setMilk({ ...milk, rate: Number(e.target.value) })} data-testid="milk-rate" />
        </div>
        <h3 className="font-display text-xl font-bold pt-2">🍲 Food at home</h3>
        <Chips multi={false} options={[["veg", "Veg"], ["nonveg", "Non-veg"], ["egg", "Eggetarian"], ["jain", "Jain"]]} value={food.diet} onChange={(v) => setFood({ ...food, diet: v })} testid="food-diet" />
        <Chips multi={false} options={["mild", "medium", "spicy"]} value={food.spice} onChange={(v) => setFood({ ...food, spice: v })} testid="food-spice" />
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" className="w-5 h-5 accent-[#6A5AE0]" checked={!!food.elders_soft} onChange={(e) => setFood({ ...food, elders_soft: e.target.checked })} /> Elders prefer soft or less spicy food</label>
        <button onClick={save} className="ul-btn ul-primary w-full" data-testid="home-settings-save">Save</button>
      </div>
    </Sheet>
  );
}

const Item = ({ to, onClick, label, testid }) => {
  const t = useT();
  label = t(label);
  const cls = "flex items-center justify-between py-3.5 border-b border-[#F3F0FF] last:border-0 font-semibold w-full text-left";
  return to ? <Link to={to} data-testid={testid} className={cls}>{label}<ChevronRight size={18} className="text-[#9C97AE]" /></Link>
    : <button onClick={onClick} data-testid={testid} className={cls}>{label}<ChevronRight size={18} className="text-[#9C97AE]" /></button>;
};

function AppLanguage({ open, onClose }) {
  const { me, refresh, setLang } = useApp();
  const t = useT();
  const pick = async (v) => { try { await api.patch(`/members/${me.me.id}`, { ui_language: v }); setLang(v); await refresh(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  const cur = me.me.ui_language || me.home.app_language || "English";
  return (
    <Sheet open={open} onClose={onClose} testid="app-language-sheet">
      <h3 className="font-display text-xl font-bold mb-1">{t("🌐 App language")}</h3>
      <p className="text-sm text-[#6B6680] mb-3">{t("Choose the language for buttons and screens. Legal pages stay in English.")}</p>
      <div className="grid gap-2">
        {UI_LANGS.map((l) => <button key={l} data-on={cur === l} data-testid={`app-lang-${l === "English" || l === "Hinglish" ? l.toLowerCase() : l === "मराठी" ? "mr" : "hi"}`} className="ul-chip !min-h-[52px] text-lg" onClick={() => pick(l)}>{l}</button>)}
      </div>
    </Sheet>
  );
}

export default function Family() {
  const t = useT();
  const { me, refresh, logout } = useApp();
  const nav = useNavigate();
  const { data: s } = useData("/summary");
  const [sheet, setSheet] = useState(null);
  const share = Object.fromEntries((s?.week?.slices || []).map((x) => [x.id, x.pct]));
  const act = async (fn, msg) => { try { await fn(); msg && toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const resetDemo = () => act(() => api.post("/demo/reset"), t("Demo restored 🌸"));
  return (
    <div>
      <Header title="Family" kicker={`${me.home.name}${me.home.city ? `, ${me.home.city}` : ""} · code ${me.home.code}`} />
      <a data-testid="invite-wa-btn" href={waUrl(inviteText(me.home.code))} target="_blank" rel="noreferrer" className="ul-btn ul-wa w-full mb-4">{t("Invite family on WhatsApp")}</a>
      <div className="space-y-2" data-testid="members-list">
        {me.members.map((m) => (
          <div key={m.id} className="ul-card p-3 flex items-center gap-3" data-testid={`member-${m.name.toLowerCase()}`}>
            <Avatar m={m} size={44} />
            <div className="flex-1 min-w-0">
              <p className="font-bold">{m.name} {m.type === "admin" && <span className="text-xs text-[#B83268]">· {t("Admin")}</span>}
                {m.type === "whatsapp" && <span className="text-xs text-[#1E7A43]"> · {t("WhatsApp only")}</span>}
                {m.type === "child" && <span className="text-xs text-[#6B6680]"> · Child, {m.age}, no login</span>}
                {m.status === "placeholder" && <span className="text-xs text-[#B45309]"> · {t("Not joined yet")}</span>}
                {m.status === "pending" && <span className="text-xs text-[#B45309]"> · {t("Wants to join")}</span>}
              </p>
              <p className="text-xs text-[#6B6680] truncate">{[t(m.relation || ""), m.type !== "child" && m.language, m.note].filter(Boolean).join(" · ")}</p>
            </div>
            {m.type !== "child" && share[m.id] != null && <span className="font-display font-extrabold text-[#6A5AE0]">{share[m.id]}%</span>}
            {me.admin && m.status === "pending" && <button className="ul-btn ul-primary !min-h-[40px] text-sm" data-testid={`approve-${m.name.toLowerCase()}`} onClick={() => act(() => api.post(`/members/${m.id}/approve`), "Approved 💜")}>{t("Approve")}</button>}
            {me.admin && m.status === "placeholder" && <a className="ul-btn ul-wa !min-h-[40px] text-sm" href={waUrl(inviteText(me.home.code))} target="_blank" rel="noreferrer">{t("Invite")}</a>}
            {me.admin && m.type === "whatsapp" && <button className="text-xs font-bold text-[#6A5AE0] underline" data-testid={`switch-app-${m.name.toLowerCase()}`} onClick={() => act(() => api.patch(`/members/${m.id}`, { type: "app" }), `${m.name} is now an app member`)}>{t("Make app member")}</button>}
            {me.admin && m.type === "app" && m.elder && !m.user_id && <button className="text-xs font-bold text-[#1E7A43] underline" onClick={() => act(() => api.patch(`/members/${m.id}`, { type: "whatsapp" }), "Switched to WhatsApp only")}>WhatsApp only</button>}
            {me.admin && m.type !== "admin" && !me.home.demo && <button className="text-[#9C97AE] text-xl px-1" aria-label="remove" data-testid={`remove-${m.name.toLowerCase()}`} onClick={() => window.confirm(`Remove ${m.name}?`) && act(() => api.delete(`/members/${m.id}`), "Removed")}>×</button>}
          </div>
        ))}
      </div>
      {me.admin && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button data-testid="add-member-btn" className="ul-btn ul-ghost" onClick={() => setSheet("add")}>{t("+ Add member")}</button>
          <button data-testid="add-child-btn" className="ul-btn ul-ghost" onClick={() => setSheet("child")}>{t("+ Add a child")}</button>
        </div>
      )}
      {me.home.demo && me.admin && <div className="mt-3"><AajiButton /></div>}
      {me.admin && (
        <div className="ul-card px-4 mt-5">
          <Item onClick={() => nav("/setup")} label={me.home.demo ? "✨ Redo home setup" : "✨ Home setup"} testid="family-setup-link" />
          <Item to="/helpers" label="🧹 Helpers" testid="family-helpers-link" />
          <Item onClick={() => setSheet("home")} label="🥛 Milk account & 🍲 food preferences" testid="family-home-settings" />
        </div>
      )}
      <p className="ul-label mt-6 mb-2">{t("Settings")}</p>
      <div className="ul-card px-4">
        <Item onClick={() => setSheet("lang")} label="🌐 App language" testid="settings-app-language" />
        <Item onClick={() => setSheet("profile")} label="🙂 My profile & language" testid="settings-profile" />
        <Item to="/legal/ai" label="✨ How AI works" testid="settings-ai" />
        <Item to="/legal/privacy" label="Privacy Policy" testid="settings-privacy" />
        <Item to="/legal/terms" label="Terms of Use" testid="settings-terms" />
        <Item to="/legal/children" label="Children" testid="settings-children" />
        <Item to="/legal/wellbeing" label="Your wellbeing" testid="settings-wellbeing" />
        <Item to="/legal/contact" label="Contact & Grievance" testid="settings-contact" />
        {me.home.demo && <Item onClick={resetDemo} label="🔄 Reset demo" testid="settings-reset-demo" />}
        <Item to="/delete-account" label="Delete account" testid="settings-delete" />
        <Item onClick={() => { logout(); nav("/login"); }} label="Log out" testid="settings-logout" />
      </div>
      <AddMember open={sheet === "add" || sheet === "child"} child={sheet === "child"} onClose={() => setSheet(null)} />
      {sheet === "profile" && <Profile open onClose={() => setSheet(null)} />}
      {sheet === "home" && <HomeSettings open onClose={() => setSheet(null)} />}
      {sheet === "lang" && <AppLanguage open onClose={() => setSheet(null)} />}
    </div>
  );
}
